import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const API = 'https://groovie-business-api-production.up.railway.app/api/v1';

const JINGLE_TYPES = [
  { value: 'station_id', label: 'Station ID', desc: 'Your station identity jingle' },
  { value: 'transition', label: 'Transition', desc: 'Between segments or songs' },
  { value: 'intro', label: 'Intro', desc: 'Show or segment opener' },
  { value: 'outro', label: 'Outro', desc: 'Show or segment closer' }
];

const STYLES = [
  { value: 'upbeat', label: 'Upbeat' }, { value: 'chill', label: 'Chill' },
  { value: 'corporate', label: 'Corporate' }, { value: 'funky', label: 'Funky' },
  { value: 'cinematic', label: 'Cinematic' }, { value: 'acoustic', label: 'Acoustic' },
  { value: 'electronic', label: 'Electronic' }
];

const MOODS = [
  { value: 'energetic', label: 'Energetic' }, { value: 'relaxed', label: 'Relaxed' },
  { value: 'professional', label: 'Professional' }, { value: 'fun', label: 'Fun' },
  { value: 'inspiring', label: 'Inspiring' }, { value: 'mysterious', label: 'Mysterious' }
];

const VOCALS = [
  { value: 'instrumental', label: 'Instrumental Only' },
  { value: 'male', label: 'Male Vocals' },
  { value: 'female', label: 'Female Vocals' }
];

const STATUS_MAP = {
  requested: { label: 'Requested', bg: '#fffbeb', color: '#d97706' },
  in_progress: { label: 'In Progress', bg: '#eff6ff', color: '#2563eb' },
  complete: { label: 'Complete', bg: '#ecfdf5', color: '#059669' },
  cancelled: { label: 'Cancelled', bg: '#f3f4f6', color: '#6b7280' }
};

const DEFAULT_REQUEST = {
  name: '', description: '', jingle_type: 'station_id',
  style: 'upbeat', mood: 'energetic', vocals: 'instrumental',
  duration: 10, special_instructions: ''
};

const DEFAULT_UPLOAD = { name: '', description: '', jingle_type: 'station_id', file: null };

const Svg = ({ type, size = 16 }) => {
  const icons = {
    play: <polygon points="5,3 19,12 5,21" fill="currentColor" />,
    stop: <rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor" />,
    plus: <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
    edit: <><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></>,
    trash: <><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></>,
    music: <><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></>,
    upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17,8 12,3 7,8"/><line x1="12" y1="3" x2="12" y2="15"/></>,
    clock: <><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></>,
    check: <><polyline points="20,6 9,17 4,12"/></>,
    x: <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
    mic: <><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></>,
    file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icons[type]}</svg>
  );
};

const inputStyle = {
  width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10,
  padding: '11px 14px', fontSize: 14, outline: 'none',
  fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const focusIn = (e) => e.target.style.borderColor = '#7c3aed';
const focusOut = (e) => e.target.style.borderColor = '#e5e7eb';

export default function Jingles() {
  const navigate = useNavigate();
  const [jingles, setJingles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [requestForm, setRequestForm] = useState({ ...DEFAULT_REQUEST });
  const [uploadForm, setUploadForm] = useState({ ...DEFAULT_UPLOAD });
  const [editForm, setEditForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const audioRef = useRef(new Audio());
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchJingles();
    return () => { audioRef.current.pause(); };
  }, [token, navigate]);

  const fetchJingles = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/jingles`, { headers });
      if (res.status === 401) { navigate('/login'); return; }
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setJingles(data.jingles || []);
    } catch (err) { console.error(err); setJingles([]); }
    finally { setLoading(false); }
  };

  const handleRequest = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const res = await fetch(`${API}/jingles`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(requestForm) });
      if (!res.ok) throw new Error('Failed');
      await fetchJingles(); setShowRequestModal(false); setRequestForm({ ...DEFAULT_REQUEST });
      alert('Jingle request submitted! Our audio team will craft it and notify you when it is ready.');
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) { alert('Please select an audio file.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', uploadForm.name);
      fd.append('jingle_type', uploadForm.jingle_type);
      if (uploadForm.description) fd.append('description', uploadForm.description);
      fd.append('file', uploadForm.file);
      const res = await fetch(`${API}/jingles/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Upload failed'); }
      await fetchJingles(); setShowUploadModal(false); setUploadForm({ ...DEFAULT_UPLOAD });
      if (fileInputRef.current) fileInputRef.current.value = '';
      alert('Jingle uploaded successfully!');
    } catch (err) { alert('Upload failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const openEditModal = (j) => {
    setEditForm({
      name: j.name || '', description: j.description || '',
      jingle_type: j.jingle_type || 'station_id', style: j.style || 'upbeat',
      mood: j.mood || 'energetic', vocals: j.vocals || 'instrumental',
      special_instructions: j.special_instructions || ''
    });
    setEditingId(j.id); setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const params = new URLSearchParams();
      Object.entries(editForm).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await fetch(`${API}/jingles/${editingId}?${params.toString()}`, { method: 'PUT', headers });
      if (!res.ok) throw new Error('Failed');
      await fetchJingles(); setShowEditModal(false); setEditingId(null);
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name, status) => {
    const action = status === 'requested' ? 'Cancel request' : 'Delete';
    if (!window.confirm(`${action} "${name}"?`)) return;
    try {
      const res = await fetch(`${API}/jingles/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Failed');
      await fetchJingles();
    } catch (err) { alert('Failed'); }
  };

  const togglePlay = (j) => {
    if (playingId === j.id) { audioRef.current.pause(); audioRef.current.src = ''; setPlayingId(null); }
    else if (j.audio_url) {
      audioRef.current.src = j.audio_url;
      audioRef.current.play().catch(() => window.open(j.audio_url, '_blank'));
      setPlayingId(j.id); audioRef.current.onended = () => setPlayingId(null);
    } else { alert('Audio not available yet.'); }
  };

  const filtered = jingles.filter(j => {
    if (filter === 'complete') return j.request_status === 'complete';
    if (filter === 'pending') return j.request_status === 'requested' || j.request_status === 'in_progress';
    if (filter === 'uploaded') return j.style === 'custom' && j.request_status === 'complete';
    return j.request_status !== 'cancelled';
  });

  const pillBtn = (selected, label, onClick) => (
    <button type="button" onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      border: 'none', cursor: 'pointer', transition: 'all 0.15s', textTransform: 'capitalize',
      background: selected ? '#7c3aed' : '#f3f4f6', color: selected ? '#fff' : '#6b7280',
    }}>{label}</button>
  );

  const typeBtn = (selected, label, desc, onClick) => (
    <button type="button" onClick={onClick} style={{
      padding: '12px 14px', borderRadius: 10, textAlign: 'left', cursor: 'pointer',
      border: selected ? '2px solid #7c3aed' : '1.5px solid #e5e7eb',
      background: selected ? '#f5f3ff' : '#fff', transition: 'all 0.15s',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{label}</div>
      {desc && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{desc}</div>}
    </button>
  );

  const modalWrap = (children) => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16, backdropFilter: 'blur(4px)' }}>
      <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', padding: 32, boxShadow: '0 25px 60px rgba(0,0,0,0.15)' }}>
        {children}
      </div>
    </div>
  );

  const modalBtns = (onCancel, submitLabel) => (
    <div style={{ display: 'flex', gap: 12, borderTop: '1px solid #f3f4f6', paddingTop: 20 }}>
      <button type="button" onClick={onCancel} style={{
        flex: 1, padding: '12px 20px', borderRadius: 10, border: '1.5px solid #e5e7eb',
        background: '#fff', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
      }}>Cancel</button>
      <button type="submit" disabled={saving} style={{
        flex: 1, padding: '12px 20px', borderRadius: 10, border: 'none',
        background: saving ? '#c4b5fd' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
        color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
        fontFamily: "'Outfit', sans-serif", boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
      }}>{saving ? 'Saving...' : submitLabel}</button>
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid #e9d5ff', borderTopColor: '#7c3aed', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto', fontFamily: "'Outfit', 'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.5px' }}>Jingles</h1>
          <p style={{ fontSize: 14, color: '#9ca3af', marginTop: 4 }}>{jingles.filter(j => j.request_status !== 'cancelled').length} jingle{jingles.length !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowUploadModal(true)} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '12px 22px', borderRadius: 12, fontSize: 14, fontWeight: 600,
            border: '2px solid #7c3aed', background: '#fff', color: '#7c3aed',
            cursor: 'pointer', fontFamily: "'Outfit', sans-serif", transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f3ff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
          >
            <Svg type="upload" size={16} /> Upload Your Own
          </button>
          <button onClick={() => setShowRequestModal(true)} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff',
            border: 'none', borderRadius: 12, padding: '12px 24px', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
            boxShadow: '0 4px 12px rgba(124,58,237,0.3)', transition: 'transform 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
          >
            <Svg type="plus" size={16} /> Request Jingle
          </button>
        </div>
      </div>

      {/* Default-brand notice (Q2): stations ship with Groovie's jingles until the business adds their own */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24,
        padding: '14px 18px', borderRadius: 12, border: '1px solid #e9d5ff', background: '#faf5ff',
      }}>
        <span style={{ fontSize: 18, lineHeight: 1 }}>🎵</span>
        <div style={{ fontSize: 13.5, color: '#6b21a8', lineHeight: 1.5 }}>
          <strong>Your station is using Groovie’s jingles.</strong> Station IDs and drops are ready out of the box —
          <span style={{ color: '#7c3aed', fontWeight: 600, cursor: 'pointer' }} onClick={() => setShowUploadModal(true)}> upload your own</span> or
          <span style={{ color: '#7c3aed', fontWeight: 600, cursor: 'pointer' }} onClick={() => setShowRequestModal(true)}> order custom</span> anytime.
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[{ key: 'all', label: 'All' }, { key: 'complete', label: 'Ready' }, { key: 'pending', label: 'Pending' }, { key: 'uploaded', label: 'My Uploads' }].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '8px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
            border: 'none', cursor: 'pointer', transition: 'all 0.15s',
            background: filter === f.key ? '#7c3aed' : '#f3f4f6',
            color: filter === f.key ? '#fff' : '#6b7280',
          }}>{f.label}</button>
        ))}
      </div>

      {/* Empty */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '64px 24px', background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb' }}>
          <div style={{ color: '#9ca3af', marginBottom: 16 }}><Svg type="music" size={48} /></div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#374151', margin: '0 0 8px' }}>
            {filter === 'all' ? 'No jingles yet' : `No ${filter} jingles`}
          </h3>
          <p style={{ fontSize: 14, color: '#9ca3af', margin: '0 0 24px' }}>Jingles give your radio station a professional sound</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => setShowUploadModal(true)} style={{
              padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 600,
              border: '2px solid #7c3aed', background: '#fff', color: '#7c3aed', cursor: 'pointer',
            }}>Upload Your Own</button>
            <button onClick={() => setShowRequestModal(true)} style={{
              padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 600,
              border: 'none', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff', cursor: 'pointer',
            }}>Request Custom Jingle</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(jingle => {
            const statusInfo = STATUS_MAP[jingle.request_status] || STATUS_MAP.requested;
            const typeInfo = JINGLE_TYPES.find(t => t.value === jingle.jingle_type) || JINGLE_TYPES[0];
            const isCustom = jingle.style === 'custom';

            return (
              <div key={jingle.id} style={{
                background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
                overflow: 'hidden', transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ padding: '20px 24px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>{jingle.name}</h3>
                        <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: statusInfo.bg, color: statusInfo.color }}>{statusInfo.label}</span>
                        <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#f3f4f6', color: '#6b7280' }}>{typeInfo.label}</span>
                        {isCustom && <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#eef2ff', color: '#4f46e5' }}>Self-Upload</span>}
                      </div>
                      {jingle.description && <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>{jingle.description}</p>}
                    </div>
                    {jingle.duration && (
                      <div style={{ textAlign: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Duration</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#374151' }}>{jingle.duration}s</div>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: '#9ca3af', marginBottom: 10, flexWrap: 'wrap' }}>
                    {!isCustom && (
                      <>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Svg type="music" size={12} /> {jingle.style}</span>
                        <span>{jingle.mood}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Svg type="mic" size={12} /> {jingle.vocals}</span>
                      </>
                    )}
                    <span style={{ fontSize: 11 }}>Requested {jingle.requested_at ? new Date(jingle.requested_at).toLocaleDateString() : 'N/A'}</span>
                    {jingle.completed_at && <span style={{ color: '#059669', fontSize: 11 }}>Completed {new Date(jingle.completed_at).toLocaleDateString()}</span>}
                    {jingle.admin_notes && <span style={{ color: '#2563eb', fontSize: 11 }}>{jingle.admin_notes}</span>}
                  </div>

                  {jingle.request_status === 'requested' && (
                    <div style={{ padding: '10px 14px', background: '#fffbeb', borderRadius: 10, border: '1px solid #fde68a', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#92400e' }}>
                      <Svg type="clock" size={14} /> Our team is working on this. We'll notify you when it's ready.
                    </div>
                  )}
                  {jingle.request_status === 'in_progress' && (
                    <div style={{ padding: '10px 14px', background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#1e40af' }}>
                      <Svg type="music" size={14} /> Your jingle is being created! Almost ready.
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', borderTop: '1px solid #f3f4f6', background: '#fafafa' }}>
                  {jingle.audio_url && jingle.request_status === 'complete' && (
                    <button onClick={() => togglePlay(jingle)} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8,
                      fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                      background: playingId === jingle.id ? '#fef2f2' : '#eff6ff',
                      color: playingId === jingle.id ? '#dc2626' : '#2563eb',
                    }}>
                      <Svg type={playingId === jingle.id ? 'stop' : 'play'} size={12} />
                      {playingId === jingle.id ? 'Stop' : 'Play'}
                    </button>
                  )}
                  <button onClick={() => openEditModal(jingle)} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8,
                    fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: '#f3f4f6', color: '#4b5563',
                  }}><Svg type="edit" size={12} /> Edit</button>
                  <div style={{ flex: 1 }} />
                  <button onClick={() => handleDelete(jingle.id, jingle.name, jingle.request_status)} style={{
                    padding: 7, borderRadius: 8, border: 'none', cursor: 'pointer', background: 'transparent', color: '#d1d5db',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#d1d5db'}
                  >{jingle.request_status === 'requested' ? <Svg type="x" size={14} /> : <Svg type="trash" size={14} />}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* REQUEST MODAL */}
      {showRequestModal && modalWrap(
        <>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Request Custom Jingle</h2>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 24px' }}>Our audio team crafts it for you and notifies you when it's ready</p>
          <form onSubmit={handleRequest}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Jingle Name *</label>
              <input type="text" required value={requestForm.name} onChange={(e) => setRequestForm({ ...requestForm, name: e.target.value })}
                placeholder="e.g. ACME Store Station ID" style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Description</label>
              <textarea rows={2} value={requestForm.description} onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                placeholder="What should it sound like?" style={{ ...inputStyle, resize: 'vertical' }} onFocus={focusIn} onBlur={focusOut} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Type</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {JINGLE_TYPES.map(t => typeBtn(requestForm.jingle_type === t.value, t.label, t.desc, () => setRequestForm({ ...requestForm, jingle_type: t.value })))}
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Style</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {STYLES.map(s => pillBtn(requestForm.style === s.value, s.label, () => setRequestForm({ ...requestForm, style: s.value })))}
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Mood</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {MOODS.map(m => pillBtn(requestForm.mood === m.value, m.label, () => setRequestForm({ ...requestForm, mood: m.value })))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Vocals</label>
                <select value={requestForm.vocals} onChange={(e) => setRequestForm({ ...requestForm, vocals: e.target.value })}
                  style={{ ...inputStyle, background: '#fff' }}>
                  {VOCALS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Duration (sec)</label>
                <input type="number" min="5" max="60" value={requestForm.duration}
                  onChange={(e) => setRequestForm({ ...requestForm, duration: parseInt(e.target.value) })}
                  style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Special Instructions</label>
              <textarea rows={3} value={requestForm.special_instructions}
                onChange={(e) => setRequestForm({ ...requestForm, special_instructions: e.target.value })}
                placeholder="e.g. Include our tagline 'Quality You Can Trust'. Use a trumpet intro."
                style={{ ...inputStyle, resize: 'vertical' }} onFocus={focusIn} onBlur={focusOut} />
            </div>
            <div style={{ background: '#f5f3ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#6d28d9' }}>
              <strong>Custom jingle.</strong> Our audio team will craft your custom jingle and notify you when it's ready.
            </div>
            {modalBtns(() => setShowRequestModal(false), 'Submit Request')}
          </form>
        </>
      )}

      {/* UPLOAD MODAL */}
      {showUploadModal && modalWrap(
        <>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Upload Your Own Jingle</h2>
          <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 24px' }}>Already have an audio file? Upload it directly.</p>
          <form onSubmit={handleUpload}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Jingle Name *</label>
              <input type="text" required value={uploadForm.name} onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                placeholder="e.g. Our Custom Station ID" style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Description</label>
              <textarea rows={2} value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Optional description" style={{ ...inputStyle, resize: 'vertical' }} onFocus={focusIn} onBlur={focusOut} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Type</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {JINGLE_TYPES.map(t => typeBtn(uploadForm.jingle_type === t.value, t.label, t.desc, () => setUploadForm({ ...uploadForm, jingle_type: t.value })))}
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Audio File *</label>
              <div onClick={() => fileInputRef.current?.click()} style={{
                border: uploadForm.file ? '2px dashed #34d399' : '2px dashed #d1d5db',
                borderRadius: 12, padding: '28px 16px', textAlign: 'center', cursor: 'pointer',
                background: uploadForm.file ? '#ecfdf5' : '#fff', transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { if (!uploadForm.file) e.currentTarget.style.borderColor = '#7c3aed'; }}
              onMouseLeave={(e) => { if (!uploadForm.file) e.currentTarget.style.borderColor = '#d1d5db'; }}
              >
                {uploadForm.file ? (
                  <div>
                    <div style={{ color: '#059669', marginBottom: 6 }}><Svg type="check" size={28} /></div>
                    <p style={{ fontSize: 14, fontWeight: 600, color: '#059669', margin: 0 }}>{uploadForm.file.name}</p>
                    <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>{(uploadForm.file.size / (1024 * 1024)).toFixed(2)} MB</p>
                  </div>
                ) : (
                  <div>
                    <div style={{ color: '#9ca3af', marginBottom: 6 }}><Svg type="upload" size={28} /></div>
                    <p style={{ fontSize: 14, color: '#4b5563', margin: 0 }}>Click to select audio file</p>
                    <p style={{ fontSize: 12, color: '#9ca3af', margin: '4px 0 0' }}>MP3, WAV, M4A, OGG &middot; Max 10MB</p>
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept=".mp3,.wav,.m4a,.ogg" style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 10 * 1024 * 1024) { alert('File too large. Max 10MB.'); return; }
                    setUploadForm({ ...uploadForm, file });
                  }
                }} />
            </div>
            {modalBtns(() => { setShowUploadModal(false); setUploadForm({ ...DEFAULT_UPLOAD }); }, 'Upload Jingle')}
          </form>
        </>
      )}

      {/* EDIT MODAL */}
      {showEditModal && modalWrap(
        <>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 24px' }}>Edit Jingle</h2>
          <form onSubmit={handleEdit}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Name</label>
              <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Description</label>
              <textarea rows={2} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                style={{ ...inputStyle, resize: 'vertical' }} onFocus={focusIn} onBlur={focusOut} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Type</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {JINGLE_TYPES.map(t => typeBtn(editForm.jingle_type === t.value, t.label, null, () => setEditForm({ ...editForm, jingle_type: t.value })))}
              </div>
            </div>
            {editForm.style !== 'custom' && (
              <>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Style</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {STYLES.map(s => pillBtn(editForm.style === s.value, s.label, () => setEditForm({ ...editForm, style: s.value })))}
                  </div>
                </div>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Special Instructions</label>
                  <textarea rows={2} value={editForm.special_instructions}
                    onChange={(e) => setEditForm({ ...editForm, special_instructions: e.target.value })}
                    style={{ ...inputStyle, resize: 'vertical' }} onFocus={focusIn} onBlur={focusOut} />
                </div>
              </>
            )}
            {modalBtns(() => setShowEditModal(false), 'Save Changes')}
          </form>
        </>
      )}
    </div>
  );
}